import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, LogOut, Mail, Phone, Clock, DollarSign, Calendar } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInquiries = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchInquiries();
  }, [navigate]);

  const fetchInquiries = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data);
    } catch (error) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-inquiries-page">
      <div className="bg-primary text-primary-foreground py-6">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin" className="mr-6">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <span className="text-2xl font-heading font-bold">MURRAY</span>
              <span className="text-2xl font-heading font-light text-accent ml-2">REALTOR</span>
              <span className="ml-4 text-sm uppercase tracking-widest">View Inquiries</span>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-primary h-10 px-6 rounded-none uppercase tracking-widest text-sm"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-heading font-bold text-primary mb-8">Contact Inquiries</h1>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground">Loading inquiries...</p>
          </div>
        ) : inquiries.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground text-lg">No inquiries yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {inquiries.map((inquiry) => (
              <Card key={inquiry.id} data-testid={`inquiry-${inquiry.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{inquiry.name}</span>
                    <span className="text-sm font-normal text-muted-foreground">
                      {new Date(inquiry.timestamp).toLocaleDateString()}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-accent" />
                      <a href={`mailto:${inquiry.email}`} className="text-primary hover:underline">
                        {inquiry.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-accent" />
                      <a href={`tel:${inquiry.phone}`} className="text-primary hover:underline">
                        {inquiry.phone}
                      </a>
                    </div>
                    {inquiry.contact_time && (
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2 text-accent" />
                        <span className="text-muted-foreground">{inquiry.contact_time}</span>
                      </div>
                    )}
                    {inquiry.budget_range && (
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 mr-2 text-accent" />
                        <span className="text-muted-foreground">{inquiry.budget_range}</span>
                      </div>
                    )}
                    {inquiry.timeline && (
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-accent" />
                        <span className="text-muted-foreground">{inquiry.timeline}</span>
                      </div>
                    )}
                  </div>
                  
                  {inquiry.interested_properties && (
                    <div className="mb-4">
                      <p className="text-sm uppercase tracking-widest text-accent font-semibold mb-2">Interested Properties:</p>
                      <p className="text-muted-foreground">{inquiry.interested_properties}</p>
                    </div>
                  )}

                  <div>
                    <p className="text-sm uppercase tracking-widest text-accent font-semibold mb-2">Message:</p>
                    <p className="text-muted-foreground leading-relaxed">{inquiry.message}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInquiries;
